import type { LoanOfficer, MonthContext, TierPlan } from '../types/commission'

export const PAR_PRICE = 100

export const tierPlans: TierPlan[] = [
  {
    id: 'plan-legacy',
    name: 'Legacy Banker',
    status: 'legacy',
    description: 'Grandfathered schedule — richer overage split, unit thresholds unchanged.',
    tiers: [
      { id: 'lg-1', name: 'Tier 1', minUnits: 0, maxUnits: 3, basisPoints: 95, unitBonus: 0, overageSplit: 0.5 },
      { id: 'lg-2', name: 'Tier 2', minUnits: 4, maxUnits: 6, basisPoints: 110, unitBonus: 150, overageSplit: 0.55 },
      { id: 'lg-3', name: 'Tier 3', minUnits: 7, maxUnits: 9, basisPoints: 125, unitBonus: 250, overageSplit: 0.6 },
      { id: 'lg-4', name: 'Tier 4', minUnits: 10, maxUnits: null, basisPoints: 140, unitBonus: 350, overageSplit: 0.65 },
    ],
  },
  {
    id: 'plan-standard',
    name: 'Standard Banker',
    status: 'standard',
    description: 'Current company schedule for all new and non-legacy bankers.',
    tiers: [
      { id: 'st-1', name: 'Tier 1', minUnits: 0, maxUnits: 4, basisPoints: 85, unitBonus: 0, overageSplit: 0.4 },
      { id: 'st-2', name: 'Tier 2', minUnits: 5, maxUnits: 7, basisPoints: 100, unitBonus: 100, overageSplit: 0.45 },
      { id: 'st-3', name: 'Tier 3', minUnits: 8, maxUnits: 11, basisPoints: 115, unitBonus: 200, overageSplit: 0.55 },
      { id: 'st-4', name: 'Tier 4', minUnits: 12, maxUnits: null, basisPoints: 130, unitBonus: 300, overageSplit: 0.6 },
    ],
  },
  {
    id: 'plan-elite',
    name: 'Elite Banker',
    status: 'elite',
    description: 'Top producer schedule — lower unit thresholds, highest basis points.',
    tiers: [
      { id: 'el-1', name: 'Tier 1', minUnits: 0, maxUnits: 2, basisPoints: 105, unitBonus: 0, overageSplit: 0.55 },
      { id: 'el-2', name: 'Tier 2', minUnits: 3, maxUnits: 5, basisPoints: 120, unitBonus: 200, overageSplit: 0.6 },
      { id: 'el-3', name: 'Tier 3', minUnits: 6, maxUnits: 8, basisPoints: 135, unitBonus: 300, overageSplit: 0.65 },
      { id: 'el-4', name: 'Tier 4', minUnits: 9, maxUnits: null, basisPoints: 150, unitBonus: 400, overageSplit: 0.7 },
    ],
  },
  {
    id: 'plan-custom-okafor',
    name: 'Custom — S. Okafor',
    status: 'custom',
    description: 'Individually negotiated schedule. Maintained separately from banker status.',
    assignedOfficerId: 'lo-okafor',
    tiers: [
      { id: 'cu-1', name: 'Tier 1', minUnits: 0, maxUnits: 3, basisPoints: 100, unitBonus: 0, overageSplit: 0.5 },
      { id: 'cu-2', name: 'Tier 2', minUnits: 4, maxUnits: 7, basisPoints: 118, unitBonus: 175, overageSplit: 0.58 },
      { id: 'cu-3', name: 'Tier 3', minUnits: 8, maxUnits: null, basisPoints: 132, unitBonus: 275, overageSplit: 0.66 },
    ],
  },
]

export const monthContext: MonthContext = {
  label: 'September 2026',
  businessDaysTotal: 22,
  businessDaysElapsed: 12,
}

export const loanOfficers: LoanOfficer[] = [
  {
    id: 'lo-hansen',
    name: 'Marcus Hansen',
    initials: 'MH',
    branch: 'Troy, MI',
    status: 'legacy',
    planId: 'plan-legacy',
    goalUnits: 10,
    closedUnits: 6,
    closedVolume: 1_840_000,
    closedOverageDollars: 21_620,
    pipeline: [
      { id: 'pl-mh-1', borrower: 'Alvarez', loanAmount: 348_000, sellPrice: 101.375, stage: 'Clear to Close', likelihood: 0.95, expectedCloseDate: 'Sep 18' },
      { id: 'pl-mh-2', borrower: 'Whitfield', loanAmount: 262_500, sellPrice: 101.125, stage: 'Docs Out', likelihood: 0.9, expectedCloseDate: 'Sep 22' },
      { id: 'pl-mh-3', borrower: 'Chen', loanAmount: 415_000, sellPrice: 100.875, stage: 'Approved', likelihood: 0.75, expectedCloseDate: 'Sep 25' },
      { id: 'pl-mh-4', borrower: 'Okonkwo', loanAmount: 298_000, sellPrice: 101.5, stage: 'Processing', likelihood: 0.5, expectedCloseDate: 'Sep 30' },
      { id: 'pl-mh-5', borrower: 'Delgado', loanAmount: 224_000, sellPrice: 100.625, stage: 'Processing', likelihood: 0.45, expectedCloseDate: 'Oct 2' },
    ],
  },
  {
    id: 'lo-navarro',
    name: 'Elena Navarro',
    initials: 'EN',
    branch: 'Troy, MI',
    status: 'elite',
    planId: 'plan-elite',
    goalUnits: 13,
    closedUnits: 8,
    closedVolume: 2_420_000,
    closedOverageDollars: 24_450,
    pipeline: [
      { id: 'pl-en-1', borrower: 'Bartlett', loanAmount: 512_000, sellPrice: 101.0, stage: 'Clear to Close', likelihood: 0.95, expectedCloseDate: 'Sep 17' },
      { id: 'pl-en-2', borrower: 'Ferrante', loanAmount: 286_000, sellPrice: 100.75, stage: 'Docs Out', likelihood: 0.9, expectedCloseDate: 'Sep 19' },
      { id: 'pl-en-3', borrower: 'Nakamura', loanAmount: 334_000, sellPrice: 101.25, stage: 'Approved', likelihood: 0.8, expectedCloseDate: 'Sep 24' },
      { id: 'pl-en-4', borrower: 'Sallinger', loanAmount: 268_000, sellPrice: 100.5, stage: 'Processing', likelihood: 0.5, expectedCloseDate: 'Sep 29' },
    ],
  },
  {
    id: 'lo-pruitt',
    name: 'Danielle Pruitt',
    initials: 'DP',
    branch: 'Tampa, FL',
    status: 'standard',
    planId: 'plan-standard',
    goalUnits: 6,
    closedUnits: 3,
    closedVolume: 690_000,
    closedOverageDollars: 9_660,
    pipeline: [
      { id: 'pl-dp-1', borrower: 'Grady', loanAmount: 244_000, sellPrice: 101.5, stage: 'Docs Out', likelihood: 0.9, expectedCloseDate: 'Sep 21' },
      { id: 'pl-dp-2', borrower: 'Moreau', loanAmount: 198_500, sellPrice: 101.25, stage: 'Approved', likelihood: 0.75, expectedCloseDate: 'Sep 26' },
      { id: 'pl-dp-3', borrower: 'Sykes', loanAmount: 312_000, sellPrice: 100.5, stage: 'Processing', likelihood: 0.45, expectedCloseDate: 'Oct 1' },
    ],
  },
  {
    id: 'lo-okafor',
    name: 'Samuel Okafor',
    initials: 'SO',
    branch: 'Phoenix, AZ',
    status: 'custom',
    planId: 'plan-custom-okafor',
    goalUnits: 8,
    closedUnits: 5,
    closedVolume: 1_310_000,
    closedOverageDollars: 11_140,
    pipeline: [
      { id: 'pl-so-1', borrower: 'Ruiz', loanAmount: 275_000, sellPrice: 100.875, stage: 'Clear to Close', likelihood: 0.95, expectedCloseDate: 'Sep 18' },
      { id: 'pl-so-2', borrower: 'Halloran', loanAmount: 236_000, sellPrice: 100.625, stage: 'Approved', likelihood: 0.75, expectedCloseDate: 'Sep 23' },
      { id: 'pl-so-3', borrower: 'Petrov', loanAmount: 401_000, sellPrice: 101.125, stage: 'Approved', likelihood: 0.7, expectedCloseDate: 'Sep 28' },
      { id: 'pl-so-4', borrower: 'Ibarra', loanAmount: 189_000, sellPrice: 100.375, stage: 'Processing', likelihood: 0.4, expectedCloseDate: 'Oct 3' },
    ],
  },
  {
    id: 'lo-reyes',
    name: 'Priya Reyes',
    initials: 'PR',
    branch: 'Phoenix, AZ',
    status: 'standard',
    planId: 'plan-standard',
    goalUnits: 5,
    closedUnits: 2,
    closedVolume: 420_000,
    closedOverageDollars: 5_250,
    pipeline: [
      { id: 'pl-pr-1', borrower: 'Lindqvist', loanAmount: 212_000, sellPrice: 101.25, stage: 'Docs Out', likelihood: 0.9, expectedCloseDate: 'Sep 22' },
      { id: 'pl-pr-2', borrower: 'Abbasi', loanAmount: 178_000, sellPrice: 100.75, stage: 'Processing', likelihood: 0.5, expectedCloseDate: 'Sep 30' },
    ],
  },
]
